import type { SupportedPostEmbedTypes, EmbedMeta } from './types'

const clearStyles = `
    * {
        box-sizing: border-box;
    }
    body, html {
        margin: 0;
        padding: 0;
        min-height: 10px;  /* must be at least a few px tall or Instagram embed won't render appropriately */
        height: 100%;
    }
    body {
        text-rendering: optimizeLegibility;
        overflow: hidden;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    iframe {
        max-width: 100%;
        min-height: 100%;  /* instagram won't render without this, resize will take care of the actual height */
    }
    iframe:after, object:after {
        clear:both;
    }
`

export const embedMappings: Partial<Record<SupportedPostEmbedTypes, EmbedMeta>> = {
    twitter: {
        buildSource: ({ postId }) => ({
            srcDoc: `
            <html lang="en">
                <head>
                    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
                    <meta name="ROBOTS" content="NOINDEX, NOFOLLOW">
                    <base target="_blank">
                    <style>
                        ${clearStyles}
                        iframe {
                            max-width: 550px;
                        }
                    </style>
                </head>
                <body>
                    <iframe id="twitter-embed-0" src="https://platform.twitter.com/embed/Tweet.html?dnt=false&embedId=twitter-widget-0&features=eyJ0ZndfdGltZWxpbmVfbGlzdCI6eyJidWNrZXQiOltdLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X2ZvbGxvd2VyX2NvdW50X3N1bnNldCI6eyJidWNrZXQiOnRydWUsInZlcnNpb24iOm51bGx9LCJ0ZndfdHdlZXRfZWRpdF9iYWNrZW5kIjp7ImJ1Y2tldCI6Im9uIiwidmVyc2lvbiI6bnVsbH0sInRmd19yZWZzcmNfc2Vzc2lvbiI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9LCJ0ZndfZm9zbnJfc29mdF9pbnRlcnZlbnRpb25zX2VuYWJsZWQiOnsiYnVja2V0Ijoib24iLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X21peGVkX21lZGlhXzE1ODk3Ijp7ImJ1Y2tldCI6InRyZWF0bWVudCIsInZlcnNpb24iOm51bGx9LCJ0ZndfZXhwZXJpbWVudHNfY29va2llX2V4cGlyYXRpb24iOnsiYnVja2V0IjoxMjA5NjAwLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X3Nob3dfYmlyZHdhdGNoX3Bpdm90c19lbmFibGVkIjp7ImJ1Y2tldCI6Im9uIiwidmVyc2lvbiI6bnVsbH0sInRmd19kdXBsaWNhdGVfc2NyaWJlc190b19zZXR0aW5ncyI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9LCJ0ZndfdXNlX3Byb2ZpbGVfaW1hZ2Vfc2hhcGVfZW5hYmxlZCI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9LCJ0ZndfdmlkZW9faGxzX2R5bmFtaWNfbWFuaWZlc3RzXzE1MDgyIjp7ImJ1Y2tldCI6InRydWVfYml0cmF0ZSIsInZlcnNpb24iOm51bGx9LCJ0ZndfbGVnYWN5X3RpbWVsaW5lX3N1bnNldCI6eyJidWNrZXQiOnRydWUsInZlcnNpb24iOm51bGx9LCJ0ZndfdHdlZXRfZWRpdF9mcm9udGVuZCI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9fQ%3D%3D&frame=false&hideCard=false&hideThread=false&id=${postId}&lang=en&origin=https%3A%2F%2Fqz.com%2Fembed%2Finset%2Fiframe%3Fid%3Dtwitter-${postId}%26autosize%3D1&sessionId=541a9fef951989530170ce503f34b4d5a7fe61b7&theme=light&widgetsVersion=2615f7e52b7e0%3A1702314776716&width=550px" allowtransparency="true" allowfullscreen="true" frameborder="0" data-tweet-id="${postId}" scrolling="no" height="800"></iframe>
                </body>
            </html>
        `,
        }),
        iframeProps: {
            height: 832,
        },
    },
    instagram: {
        buildSource: ({ postId }) => ({
            srcDoc: `
            <html lang="en">
                <head>
                    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
                    <meta name="ROBOTS" content="NOINDEX, NOFOLLOW">
                    <base target="_blank">
                    <style>
                        ${clearStyles}
                        @media only screen and (max-width: 480px) {
                            .instagram-media {
                                position: relative;
                                height: 100vh;
                            }
                        } /* fix for instagram embed height in iOS Safari */
                    </style>
                </head>
                <body>
                    <style>
                    .instagram-media {
                        box-shadow: none !important;
                        border: 1px solid rgba(0,0,0,0.1);
                        margin: 0 !important;
                    }
                    </style>
                    <iframe class="instagram-media instagram-media-rendered" id="instagram-embed-0" src="https://www.instagram.com/p/${postId}/embed/" allowtransparency="true" allowfullscreen="true" frameborder="0" data-instgrm-payload-id="instagram-media-payload-0" height="550" scrolling="no"></iframe>
                </body>
            </html>
        `,
        }),
        iframeProps: {
            height: 600,
        },
    },
    facebook: {
        buildSource: ({ postId }) => ({
            srcDoc: `
            <html lang="en">
                <head>
                    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
                    <meta name="ROBOTS" content="NOINDEX, NOFOLLOW">
                    <base target="_blank">
                    <style>
                        ${clearStyles}
                    </style>
                    <script async defer crossorigin="anonymous" src="https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v22.0"></script>
                </head>
                <body>
                    <div id="fb-root"></div>
                    <div class="fb-post" data-href="${postId}" data-width="500" data-show-text="true"></div>
                </body>
            </html>
            `,
        }),
        iframeProps: {
            height: 793,
        },
    },
    tumblr: {
        buildSource: ({ postId, secondaryId }) => ({
            srcDoc: `
            <html lang="en">
                <head>
                    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
                    <meta name="ROBOTS" content="NOINDEX, NOFOLLOW">
                    <base target="_blank">
                    <style>
                        ${clearStyles}
                        iframe {
                            width: 542px;
                        }
                    </style>
                </head>
                <body>
                    <iframe title="Tumblr post" frameborder="0" allowfullscreen="true" src="https://embed.tumblr.com/embed/post/${secondaryId}/${postId}/v2?width=542&language=en_US"></iframe>
                </body>
            </html>
            `,
        }),
        iframeProps: {
            height: 870,
        },
    },
    'document-cloud': {
        buildSource: ({ postId }) => ({
            srcDoc: `
            <html lang="en">
                <head>
                    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
                    <meta name="ROBOTS" content="NOINDEX, NOFOLLOW">
                    <base target="_blank">
                    <style>
                        ${clearStyles}
                        iframe {
                            width: 100%;
                        }
                    </style>
                </head>
                <body>
                    <iframe frameborder="0" allowfullscreen="true" sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox" src="https://embed.documentcloud.org/documents/${postId}/?embed=1&embed=1"></iframe>
                </body>
            </html>
            `,
        }),
        iframeProps: {
            height: 1000,
        },
    },
}
